import { apiClient } from './client';
import type { components } from './schema.gen';

/**
 * Direct re-exports of the wire types — the UI uses them throughout, so
 * giving them short stable names avoids deeply-nested `components[…]`
 * declarations in every consuming file.
 */
export type Entrepreneur = components['schemas']['EntrepreneurView'];
export type EventRecord = components['schemas']['EventView'];

export type ReconStatus = '' | 'OK' | 'PENDING_REVIEW' | 'CONFLICT';

/** Qui a enrôlé le commerçant. */
export type RecruitmentSource = '' | 'MAFA_TERRAIN' | 'ORANGE_PARTENAIRE' | 'ID30' | 'UNKNOWN';

export type SearchFilters = {
  phone?: string;
  name?: string;
  commune?: string;
  sector?: string;
  reconStatus?: ReconStatus;
  /** Origine de recrutement — miroir de l'enum de l'API. */
  recruitmentSource?: RecruitmentSource;
  /** Survenance du dernier événement (occurred_at) — ISO 8601, borne incluse. */
  lastEventFrom?: string;
  /** Survenance du dernier événement (occurred_at) — ISO 8601, borne exclue. */
  lastEventTo?: string;
  /** Enregistrement du dernier événement (recorded_at) — ISO 8601, borne incluse. */
  recordedFrom?: string;
  /** Enregistrement du dernier événement (recorded_at) — ISO 8601, borne exclue. */
  recordedTo?: string;
  /** Clé de tri serveur (vide = récence d'événement). */
  sort?: SortKey;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
};

/** Colonnes triables côté serveur — miroir de l'enum `sort` de l'API. */
export type SortKey =
  | 'name'
  | 'commune'
  | 'activity'
  | 'status'
  | 'eventCount'
  | 'lastEvent'
  | 'recorded';

export type SearchResult = {
  items: Entrepreneur[];
  total: number;
};

/**
 * GET /v1/entrepreneurs — the search endpoint. The backend returns
 * { items, total } where items is paginated and total is the count
 * before slicing. The signature is intentionally flat — the table page
 * passes individual filters, not a query-string blob.
 *
 * Every filter here is applied server-side, *before* pagination: the page
 * must never refine the returned rows locally, or the grid would filter
 * the current page only while `total` still counts the whole set.
 */
export async function searchEntrepreneurs(filters: SearchFilters = {}): Promise<SearchResult> {
  const { data, error } = await apiClient.GET('/v1/entrepreneurs', {
    params: {
      query: {
        phone: filters.phone ?? '',
        name: filters.name ?? '',
        commune: filters.commune ?? '',
        sector: filters.sector ?? '',
        reconStatus: filters.reconStatus ?? '',
        recruitmentSource: filters.recruitmentSource ?? '',
        lastEventFrom: filters.lastEventFrom ?? '',
        lastEventTo: filters.lastEventTo ?? '',
        recordedFrom: filters.recordedFrom ?? '',
        recordedTo: filters.recordedTo ?? '',
        sort: filters.sort ?? '',
        order: filters.order ?? 'desc',
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      },
    },
  });
  if (error) {
    // Le serveur explique ce qui bloque — filtre invalide, jeton expiré,
    // indisponibilité. Remplacer ce message par « searchEntrepreneurs failed »
    // privait l'écran de la seule information exploitable, et le tableau vide
    // se lisait alors comme « aucun résultat » au lieu de « la requête a
    // échoué ».
    const detail = (error as { detail?: string; title?: string } | undefined)?.detail;
    throw new Error(detail ?? "la recherche des dossiers a échoué");
  }
  return {
    items: (data?.items ?? []) as Entrepreneur[],
    total: data?.total ?? 0,
  };
}

/**
 * GET /v1/entrepreneurs/{phone} — returns the consolidated 360° view.
 * Returns null when the phone is not known (404), so the caller can
 * render an empty-state without try/catch.
 */
export async function getEntrepreneur(phone: string): Promise<Entrepreneur | null> {
  const { data, response } = await apiClient.GET('/v1/entrepreneurs/{phone}', {
    params: { path: { phone } },
  });
  if (response.status === 404) return null;
  if (!data) return null;
  return data as Entrepreneur;
}

export type TimelineFilters = {
  type?: string;
  from?: string;
  to?: string;
  limit?: number;
};

/**
 * GET /v1/entrepreneurs/{phone}/timeline — paginated event journal. The
 * backend already orders newest-first; the UI displays them as-is.
 */
export async function getEntrepreneurTimeline(
  phone: string,
  filters: TimelineFilters = {},
): Promise<EventRecord[]> {
  const { data, error } = await apiClient.GET('/v1/entrepreneurs/{phone}/timeline', {
    params: {
      path: { phone },
      query: {
        type: filters.type ?? '',
        from: filters.from ?? '',
        to: filters.to ?? '',
        limit: filters.limit ?? 100,
      },
    },
  });
  if (error) return [];
  return (data?.events ?? []) as EventRecord[];
}
